import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ConfigProvider } from 'antd';
import frFR from 'antd/locale/fr_FR';
import enUS from 'antd/locale/en_US';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';
import { I18nextProvider } from 'react-i18next';
import i18nInstance, {
  AppLocale,
  DEFAULT_APP_LOCALE,
  isAppLocale,
  persistLocale,
  resolveInitialLocale,
} from '../i18n';

interface LocaleContextValue {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

const ANT_LOCALES = {
  fr: frFR,
  en: enUS,
} as const;

function applyDayjsLocale(locale: AppLocale): void {
  dayjs.locale(locale);
}

export interface LocaleProviderProps {
  children: React.ReactNode;
  /** Thème Ant Design (passé au ConfigProvider interne). */
  theme?: React.ComponentProps<typeof ConfigProvider>['theme'];
}

/**
 * Fournit la locale UI, synchronise i18next + Ant Design + dayjs,
 * et remplace le ConfigProvider locale fixe.
 */
export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children, theme }) => {
  const [locale, setLocaleState] = useState<AppLocale>(() => resolveInitialLocale());

  useEffect(() => {
    applyDayjsLocale(locale);
    if (i18nInstance.language !== locale) {
      void i18nInstance.changeLanguage(locale);
    }
  }, [locale]);

  const setLocale = useCallback((next: AppLocale) => {
    if (!isAppLocale(next)) return;
    setLocaleState((prev) => {
      if (prev === next) return prev;
      persistLocale(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ locale, setLocale }),
    [locale, setLocale]
  );

  return (
    <I18nextProvider i18n={i18nInstance}>
      <LocaleContext.Provider value={value}>
        <ConfigProvider locale={ANT_LOCALES[locale]} theme={theme}>
          {children}
        </ConfigProvider>
      </LocaleContext.Provider>
    </I18nextProvider>
  );
};

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error('useLocale must be used within LocaleProvider');
  }
  return ctx;
}

/**
 * Synchronise la langue avec le profil utilisateur :
 * - si `preferredLocale` est défini → l’applique une fois (priorité multi-appareils)
 * - sinon → la modale PreferredLocaleModal demande un choix explicite (pas de seed silencieux)
 *
 * Ne dépend PAS de `locale` : sinon un clic FR est écrasé par l’ancienne
 * préférence EN avant que le patch profil / Firestore ne soit pris en compte.
 */
export const LocaleAuthSync: React.FC<{
  userId?: string | null;
  preferredLocale?: AppLocale | null;
  /** True une fois le profil utilisateur chargé (évite un seed prématuré). */
  profileReady?: boolean;
}> = ({ userId, preferredLocale, profileReady = false }) => {
  const { setLocale } = useLocale();
  const lastAppliedKey = useRef<string | null>(null);

  useEffect(() => {
    if (!userId) {
      lastAppliedKey.current = null;
    }
  }, [userId]);

  useEffect(() => {
    if (!profileReady || !userId) return;

    if (!isAppLocale(preferredLocale)) return;

    const key = `${userId}:${preferredLocale}`;
    if (lastAppliedKey.current === key) return;
    lastAppliedKey.current = key;
    setLocale(preferredLocale);
  }, [profileReady, userId, preferredLocale, setLocale]);

  return null;
};

export { DEFAULT_APP_LOCALE };
